/**
 * Review system stub utilities
 * These are placeholder implementations for future review features
 */

import { supabase } from "@/integrations/supabase/client";

export interface Review {
  id: string;
  rating: number;
  comment: string | null;
  userId: string | null;
  productId: string | null;
  menuItemId: string | null;
  createdAt: string;
  isApproved: boolean;
}

export interface ReviewEligibility {
  canReview: boolean;
  hasPurchased: boolean;
  hasReviewed: boolean;
}

export interface FavoriteReviewResult {
  success: boolean;
  data?: unknown[];
  stats?: { total: number; timeframeCount: number; uniqueUsers: number };
  error?: Error | string;
}

export interface ReviewCreateResult {
  success: boolean;
  error?: Error | string;
  message?: string;
}

export interface ImageUploadResult {
  success: boolean;
  url?: string;
  error?: Error | string;
}

/**
 * Check if user is eligible to review a product
 * Stub implementation - returns default eligible state
 */
export function useReviewEligibility(
  _user: unknown,
  _productId: string,
  _enabled: boolean,
): ReviewEligibility & { orderId?: string; orderItemId?: string } {
  return {
    canReview: false,
    hasPurchased: false,
    hasReviewed: false,
    orderId: undefined,
    orderItemId: undefined,
  };
}

/**
 * Fetch user's favorite reviews
 * Returns reviews for items the user has favorited
 */
export async function fetchUserFavoriteReviews(options: {
  userId: string;
}): Promise<FavoriteReviewResult> {
  if (!options.userId) {
    return { success: true, data: [] };
  }

  const { data, error } = await supabase
    .from("product_reviews")
    .select("*")
    .eq("user_id", options.userId)
    .eq("is_approved", true)
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching favorite reviews:", error);
    return { success: false, error: error.message };
  }

  return { success: true, data: data || [] };
}

/**
 * Fetch admin reviews for moderation
 */
export async function fetchAdminFavoriteReviews(
  _options: {
    timeframe?: string;
  } = {},
): Promise<FavoriteReviewResult> {
  const { data, error } = await supabase
    .from("product_reviews")
    .select("*")
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching admin reviews:", error);
    return { success: false, error: error.message };
  }

  const total = data?.length || 0;
  const uniqueUsers = new Set(data?.map((r) => r.user_id)).size;

  return {
    success: true,
    data: data || [],
    stats: {
      total,
      timeframeCount: total,
      uniqueUsers,
    },
  };
}

/**
 * Upload an image for a review
 * Stub implementation - returns null
 */
export async function uploadReviewImage(
  _file: File,
  _userId?: string,
): Promise<ImageUploadResult> {
  // Stub - no storage bucket configured
  console.warn("Review image upload not yet implemented");
  return { success: false, error: "Image upload not implemented" };
}

/**
 * Create a new review (for FavoriteCommentsPanel)
 */
export async function createReview(review: {
  userId: string;
  productId?: string;
  menuItemId?: string;
  itemType?: string;
  orderId?: string;
  orderItemId?: string;
  rating?: number;
  reviewText?: string;
  reviewImages?: string[];
  source?: string;
  favoriteIsGeneral?: boolean;
  favoriteTargetLabel?: string;
}): Promise<ReviewCreateResult> {
  const { error } = await supabase
    .from("product_reviews")
    .insert({
      rating: review.rating || 5,
      comment: review.reviewText || null,
      user_id: review.userId,
      product_id: review.productId || null,
      menu_item_id: review.menuItemId || null,
      is_approved: false,
    })
    .select()
    .single();

  if (error) {
    console.error("Error creating review:", error);
    return { success: false, error: error.message };
  }

  return { success: true };
}
